<template>
  <div class="page-container">
    <div class="content-wrapper">
        <!-- Page Header -->
        <div class="page-header" v-if="isBenefitModuleEnabled">
            <div class="header-left">
                <h1 class="page-title">{{ page.cardTitle }}</h1>
                <p class="page-subtitle">Configure the core details and visual appearance of this benefit.</p>
            </div>
            <div class="header-actions">
                <button type="button" class="btn-cancel" @click="cancel">Cancel</button>
                <button type="button" class="btn-save" @click="change" :disabled="page.isLoading">
                    <span v-if="page.isLoading"><i class="fas fa-spinner fa-spin me-2"></i> Saving...</span>
                    <span v-else><i class="fas fa-check-circle me-2"></i> Save Benefit</span>
                </button>
            </div>
        </div>

        <div v-if="error" class="alert alert-danger mb-4 shadow-sm" role="alert">
            <i class="fas fa-exclamation-triangle me-2"></i> {{ error }}
        </div>

        <!-- Feature Disabled State -->
        <div v-if="!isBenefitModuleEnabled" class="feature-disabled-container mt-4">
            <div class="disabled-content">
                <i class="fas fa-toggle-off fa-3x primary-text mb-3"></i>
                <h4>Benefit Module Disabled</h4>
                <p>The Benefit Module has been disabled by System Administration. Please contact System Owner to enable this feature.</p>
            </div>
        </div>

        <template v-if="isBenefitModuleEnabled">
        <!-- Main Content -->
        <div class="main-grid">
            <!-- Left Column: Form -->
            <div class="grid-column main-col">
                <div class="content-card">
                    <div class="card-section">
                        <h3 class="section-title">General Information</h3>
                        <p class="section-desc">Set the name and scope for this benefit.</p>

                        <div class="form-grid">
                            <!-- Merchant (Only if needed) -->
                            <div v-if="!selectedBusinessProfile.merchant_id && !selectedBusinessProfile.branch_id && !selectedBusinessProfile.corporate_id" class="form-item full">
                                <label>Target Merchant</label>
                                <o-select v-model="benefit.merchant_id" :options="merchants" placeholder="Select Merchant"></o-select>
                                <div v-if="validation.merchant_id" class="error-text">{{ validation.merchant_id[0] }}</div>
                            </div>

                            <!-- Name -->
                            <div class="form-item full">
                                <label>Benefit Name <span class="required">*</span></label>
                                <input type="text" v-model="benefit.name" class="modern-input" placeholder="e.g. Complimentary Sunday Brunch">
                                <div v-if="validation.name" class="error-text">{{ validation.name[0] }}</div>
                            </div>

                            <!-- Tiers as Checkbox Cards -->
                            <div class="form-item full" v-if="selectedBusinessProfile.corporate_id">
                                <label>Eligible Tier <span class="required">*</span></label>
                                <div class="tier-radio-group">
                                    <!-- All Tiers Card -->
                                    <label class="tier-radio-card" :class="{ 'active': allTiersSelected }" @click="selectAllTiers">
                                        <div class="tier-card-content">
                                            <div class="checkbox-indicator" :class="{ 'checked': allTiersSelected }">
                                                <i v-if="allTiersSelected" class="fas fa-check check-icon"></i>
                                            </div>
                                            <span class="tier-name">All Tiers</span>
                                        </div>
                                    </label>
                                    <!-- Individual Tier Cards (always visible) -->
                                    <label 
                                        v-for="tier in tiers" 
                                        :key="tier.id" 
                                        class="tier-radio-card" 
                                        :class="{ 'active': isTierActive(tier.id) }"
                                        @click="toggleTier(tier.id)"
                                    >
                                        <div class="tier-card-content">
                                            <div class="checkbox-indicator" :class="{ 'checked': isTierActive(tier.id) }">
                                                <i v-if="isTierActive(tier.id)" class="fas fa-check check-icon"></i>
                                            </div>
                                            <span class="tier-name">{{ tier.name }}</span>
                                        </div>
                                    </label>
                                </div>
                                <div v-if="validation.member_tier_id" class="error-text">{{ validation.member_tier_id[0] }}</div>
                            </div>

                            <!-- Description -->
                            <div class="form-item full">
                                <label>Description <span class="required">*</span></label>
                                <textarea 
                                    v-model="benefit.description" 
                                    class="modern-textarea" 
                                    rows="5" 
                                    placeholder="Provide a clear description of what this benefit offers to members..."
                                ></textarea>
                                <div v-if="validation.description" class="error-text">{{ validation.description[0] }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right Column: Visuals -->
            <div class="grid-column side-col">
                <div class="content-card">
                    <h3 class="section-title">Benefit Media <span class="required">*</span></h3>
                    <p class="section-desc">An image that represents this benefit.</p>

                    <div class="visual-upload-section">
                        <!-- Main Media Container (Dashed border, pure image inside, clickable to upload) -->
                        <div class="main-media-container clickable" @click="triggerFileInput">
                            <img 
                                v-if="benefit.media_type === 'default' || !image.url" 
                                :src="currentThemeDefaultImageUrl" 
                                alt="Benefit Default Media" 
                                class="pure-media-preview"
                            >
                            <img 
                                v-else 
                                :src="image.url" 
                                alt="Benefit Custom Media" 
                                class="pure-media-preview"
                            >
                            <div class="change-overlay">
                                <i class="fas fa-camera"></i>
                                <span>Change Photo</span>
                            </div>
                            <input 
                                type="file" 
                                ref="fileInput" 
                                accept="image/png, image/jpeg, image/jpg, image/webp" 
                                class="file-input-hidden" 
                                @change="handleFileUpload"
                            >
                        </div>

                        <!-- Info help text below container -->
                        <div class="info-help-text mt-2 text-center text-muted small">
                            <i class="fas fa-info-circle me-1"></i>
                            <span>Click on the image above to change it</span>
                        </div>

                        <!-- Reset to default image option if custom image is active -->
                        <div v-if="benefit.media_type === 'image' && image.url" class="mt-2">
                            <button 
                                type="button" 
                                class="dashed-action-btn reset-btn" 
                                @click.stop="switchToDefaultMedia"
                            >
                                <i class="fas fa-redo me-2"></i> Reset to default theme image
                            </button>
                        </div>

                        <div v-if="validation.avatar" class="error-text text-center mt-2">{{ validation.avatar[0] }}</div>
                        <div v-if="validation.default_image_url" class="error-text text-center mt-2">{{ validation.default_image_url[0] }}</div>
                        <div v-if="validation.media_type" class="error-text text-center mt-2">{{ validation.media_type[0] }}</div>
                    </div>
                </div>
            </div>
        </div>
        </template>
    </div>

    <!-- Global Loading Overlay -->
    <div v-if="page.isLoading" class="global-loader">
        <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
        </div>
    </div>
  </div>
</template>

<script>
import { OSelect } from '@/components/OmnihControl';
import { reactive, ref, onMounted, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import store from '@/store'
import { useToast } from '@/composables/useToast'
import { BENEFIT_DEFAULT_IMAGES } from '@/constants/benefitDefaultImages'
import { resolveRecommendedDefaultImageId } from '@/helpers/themeImageResolver'

export default {
    name: 'AddBenefit',
    components: {
        OSelect
    },
    setup(){
        const page = reactive ({
            isLoading: false,
            cardTitle: 'Create New Benefit'
        });
        const isLoaded = ref(false);
        
        const benefit = reactive({
            name: '',
            merchant_id: '',
            corporate_id: '',
            branch_id: '',
            avatar: '',
            media_type: null,
            default_image_url: null,
            selected_tier_ids: [],
            description:''
        });

        const allTiersSelected = ref(false);
        const tiers = computed(() => store.state.MemberTier.allTiers || []);

        const image = reactive({
            url: ""
        });

        const validation = ref({});
        const router = useRouter();
        const route = useRoute();
        const merchants = ref([]);

        const { showToast } = useToast();

        const selectedBusinessProfile = reactive({
            corporate_id: '',
            branch_id: '',
            merchant_id: ''
        })

        const error = ref('');

        const isBenefitModuleEnabled = computed(() => {
            return (store.state.FeatureFlag?.benefitModule?.flag || 'ACTIVE') === 'ACTIVE'
        });

        const recommendedImageId = ref(null);
        const fileInput = ref(null);

        const currentThemeDefaultImageUrl = computed(() => {
            const recommendedId = resolveRecommendedDefaultImageId(store.state.ThemeCorporate?.themeCorporate);
            const imgObj = BENEFIT_DEFAULT_IMAGES.find(img => img.id === recommendedId);
            return imgObj ? imgObj.image_url : BENEFIT_DEFAULT_IMAGES[0].image_url;
        });

        function triggerFileInput() {
            if (fileInput.value) {
                fileInput.value.click();
            }
        }

        function handleFileUpload(e) {
            const files = e.target.files || e.dataTransfer?.files;
            if (!files || !files.length) return;
            const file = files[0];
            
            const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
            if (!validTypes.includes(file.type)) {
                alert('Invalid file format. Please use PNG, JPG, or WEBP.');
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                alert('File is too large. Maximum size is 5MB.');
                return;
            }

            const reader = new FileReader();
            reader.onload = (evt) => {
                setBenefitMedia('image', evt.target.result);
            };
            reader.readAsDataURL(file);
        }

        function switchToDefaultMedia() {
            setBenefitMedia('default', currentThemeDefaultImageUrl.value);
        }

        function setBenefitMedia(type, urlOrFile) {
            benefit.media_type = type;
            if (type === 'default') {
                benefit.default_image_url = urlOrFile || currentThemeDefaultImageUrl.value;
                benefit.avatar = '';
                image.url = '';
            } else {
                benefit.avatar = urlOrFile;
                benefit.default_image_url = null;
                if (urlOrFile && urlOrFile.startsWith('data:image')) {
                    image.url = urlOrFile;
                }
            }
        }

        onMounted(async () => {
            page.isLoading = true;
            await loadBusinessProfile();

            const theme = store.state.ThemeCorporate?.themeCorporate;
            if (theme && theme.primary_button) {
                recommendedImageId.value = resolveRecommendedDefaultImageId(theme);
            }

            if (route.params.id) {
                await loadState(route.params.id);
            } else {
                setBenefitMedia('default', currentThemeDefaultImageUrl.value);
            }
            
            isLoaded.value = true;
            page.isLoading = false;
        });

        watch(() => selectedBusinessProfile.corporate_id, (newVal) => {
            if (newVal) {
                store.dispatch('MemberTier/read_all', newVal);
            }
        });

        async function loadState(id) {
            page.isLoading = true;
            try {
                const data = await store.dispatch('Benefit/read_single', id);
                benefit.id = data.id;
                benefit.name = data.name;
                benefit.merchant_id = data.merchant_id;
                benefit.description = data.desc;
                benefit.media_type = data.media_type;
                benefit.default_image_url = data.default_image_url;
                
                if (data.media_type === 'default') {
                    benefit.media_type = 'default';
                    benefit.default_image_url = currentThemeDefaultImageUrl.value;
                    image.url = '';
                } else if (data.avatar && data.avatar.image_url) {
                    benefit.media_type = 'image';
                    image.url = data.avatar.image_url;
                    benefit.avatar = data.avatar.image_url;
                } else {
                    setBenefitMedia('default', currentThemeDefaultImageUrl.value);
                }
                
                page.cardTitle = 'Edit Benefit';

                // Load tier selection state from pivot table
                const benefitTiers = data.benefit_tiers || [];
                const hasAllTiersFlag = benefitTiers.some(bt => bt.is_all_tiers);

                if (!hasAllTiersFlag && data.tiers && data.tiers.length > 0) {
                    // New system: has specific tiers assigned via pivot table
                    allTiersSelected.value = false;
                    benefit.selected_tier_ids = data.tiers.map(t => t.id);
                } else if (!hasAllTiersFlag && benefitTiers.length === 0 && data.tier_id) {
                    // Backward compat: old benefit with tier_id directly on benefits table (no pivot record yet)
                    allTiersSelected.value = false;
                    benefit.selected_tier_ids = [data.tier_id];
                } else {
                    // No tier assignment (tier_id=null, no pivot) = All Tiers
                    allTiersSelected.value = true;
                    if (tiers.value.length > 0) {
                        benefit.selected_tier_ids = tiers.value.map(t => t.id);
                    }
                }
            } catch (err) {
                // Silently handle error for production lint rules
                page.isLoading = false;
            } finally {
                page.isLoading = false;
            }
        }

        function loadBusinessProfile() {
            return store.dispatch('Auth/getBusinessProfile').then((businessProfile) => {
                selectedBusinessProfile.corporate_id = businessProfile.corporate_id;
                selectedBusinessProfile.branch_id = businessProfile.branch_id;
                selectedBusinessProfile.merchant_id = businessProfile.merchant_id;
                benefit.merchant_id = businessProfile.merchant_id;
                benefit.corporate_id = businessProfile.corporate_id;
                benefit.branch_id = businessProfile.branch_id;

                if (businessProfile.merchant_id == null) {
                    loadMerchants();
                }
            });
        }

        function loadMerchants() {
            const filter = {};
            if (selectedBusinessProfile.branch_id) {
                filter.branch_id = selectedBusinessProfile.branch_id;
            }
            store.commit('Merchant/add_filter', {filter});
            store.dispatch('Merchant/read').then(() => {
                merchants.value = [...store.state.Merchant.merchants.map((merch)=>({'value':merch.id,'text':merch.name}))]
            })
        }

        function isTierActive(tierId) {
            return allTiersSelected.value || benefit.selected_tier_ids.includes(tierId);
        }

        function selectAllTiers() {
            if (allTiersSelected.value) {
                allTiersSelected.value = false;
                benefit.selected_tier_ids = [];
            } else {
                allTiersSelected.value = true;
                benefit.selected_tier_ids = tiers.value.map(t => t.id);
            }
        }

        function toggleTier(tierId) {
            const idx = benefit.selected_tier_ids.indexOf(tierId);
            
            if (idx === -1) {
                benefit.selected_tier_ids.push(tierId);
            } else {
                benefit.selected_tier_ids.splice(idx, 1);
            }

            // Sync with All Tiers check
            if (benefit.selected_tier_ids.length === tiers.value.length && tiers.value.length > 0) {
                allTiersSelected.value = true;
            } else {
                allTiersSelected.value = false;
            }
        }

        function change(){
            validation.value = {};
            error.value = '';

            let hasError = false;
            if (!benefit.name || benefit.name.trim() === '') {
                validation.value.name = ['Benefit name is required.'];
                hasError = true;
            }
            if (!benefit.description || benefit.description.trim() === '') {
                validation.value.description = ['Description is required.'];
                hasError = true;
            }
            if (!selectedBusinessProfile.merchant_id && !selectedBusinessProfile.branch_id && !selectedBusinessProfile.corporate_id && !benefit.merchant_id) {
                 validation.value.merchant_id = ['Target Merchant is required.'];
                 hasError = true;
            }
            if (route.name === 'BenefitForm' && !benefit.avatar && !benefit.default_image_url) {
                validation.value.avatar = ['Benefit Media is required. Please upload an image or choose a default icon.'];
                hasError = true;
            }

            if (selectedBusinessProfile.corporate_id && !allTiersSelected.value && benefit.selected_tier_ids.length === 0) {
                validation.value.member_tier_id = ['Please select at least one tier that is eligible to receive this benefit.'];
                hasError = true;
            }

            if (hasError) {
                error.value = 'Please check the form for errors.';
                return;
            }

            page.isLoading = true;
            // Clone secara eksplisit agar array tidak hilang dari Vue reactive proxy
            const data = {
                name: benefit.name,
                merchant_id: benefit.merchant_id,
                corporate_id: benefit.corporate_id,
                branch_id: benefit.branch_id,
                media_type: benefit.media_type || 'image',
                default_image_url: benefit.media_type === 'default' ? currentThemeDefaultImageUrl.value : benefit.default_image_url,
                avatar: benefit.avatar,
                description: benefit.description,
                is_all_tiers: allTiersSelected.value ? 1 : 0,
                selected_tier_ids: allTiersSelected.value ? [] : [...benefit.selected_tier_ids],
            };
            if (benefit.id) data.id = benefit.id;

            if(route.name != 'BenefitForm'){
                // Only send avatar if it's a freshly uploaded base64 image.
                // If avatar is an existing URL or empty, exclude it so the backend
                // doesn't try to re-process it as base64.
                if (data.media_type === 'default' || !data.avatar || !String(data.avatar).startsWith('data:image')) {
                    delete data.avatar;
                }
                store.dispatch('Benefit/update', {data}).then(() => {
                    showToast({
                        type: 'success',
                        title: 'Updated Successfully',
                        message: `Benefit "${data.name}" has been updated.`
                    });
                    router.push({ name: 'Benefit' })
                }).catch(handleError).finally(() => page.isLoading = false);
            } else {
                store.dispatch('Benefit/create', {data}).then(() => {
                    showToast({
                        type: 'success',
                        title: 'Created Successfully',
                        message: `Benefit "${data.name}" has been created.`
                    });
                    router.push({ name: 'Benefit' })
                }).catch(handleError).finally(() => page.isLoading = false);
            }
        }

        function handleError(err) {
            if (err.response && err.response.data.error) {
                try {
                    if (typeof err.response.data.error === 'string') {
                         validation.value = JSON.parse(err.response.data.error)
                    } else {
                         validation.value = err.response.data.error
                    }
                } catch (e) {
                    error.value = err.response.data.message || 'Validation failed'
                }
            } else {
                error.value = err.response?.data?.message || 'An unexpected error occurred'
            }
        }

        function imageHandling(img) {
            setBenefitMedia('image', img);
        }

        function cancel() {
            router.push({ name: 'Benefit' });
        }

        return {
            page,
            benefit,
            validation,
            router,
            merchants,
            loadMerchants,
            selectedBusinessProfile,
            image,
            imageHandling,
            isLoaded,
            cancel,
            error,
            change,
            tiers,
            allTiersSelected,
            selectAllTiers,
            toggleTier,
            isTierActive,
            isBenefitModuleEnabled,
            currentThemeDefaultImageUrl,
            recommendedImageId,
            setBenefitMedia,
            fileInput,
            triggerFileInput,
            handleFileUpload,
            switchToDefaultMedia
        }
    }
}
</script>

<style scoped>
/* Main Layout */
.page-container {
    width: 100%;
    min-height: calc(100vh - 64px);
    background-color: #f1f4f9;
    padding: 24px 40px;
}

.content-wrapper {
    max-width: 1240px;
    margin: 0 auto;
}

/* Feature Disabled State */
.feature-disabled-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #e5e7eb;
    padding: 2rem;
}

.disabled-content {
    text-align: center;
    color: #6b7280;
}

.disabled-content h4 {
    color: #374151;
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.disabled-content p {
    margin: 0;
    font-size: 0.95rem;
}

/* Header */
.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 32px;
}

.page-title {
    font-size: 28px;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.5px;
}

.page-subtitle {
    color: #64748b;
    font-size: 15px;
    margin: 0;
}

.header-actions {
    display: flex;
    flex-direction: row; 
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
}

/* Buttons */
.btn-cancel {
    padding: 10px 24px;
    border: 1px solid #e2e8f0;
    background: white;
    color: #475569;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;
}

.btn-cancel:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
}

.btn-save {
    padding: 10px 24px;
    background: var(--primary-color, #fc6666); 
    border: none;
    color: white;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.3s ease, box-shadow 0.3s ease;
    display: flex;
    align-items: center;
    box-shadow: 0 4px 6px -1px var(--secondary-color, rgba(252, 102, 102, 0.2));
}

.btn-save:hover {
    filter: brightness(0.9);
}

.btn-save:disabled {
    background: #cbd5e1;
    box-shadow: none;
    cursor: not-allowed;
}

/* Grid System */
.main-grid {
    display: grid;
    grid-template-columns: 1fr 380px;
    gap: 24px;
}

.content-card {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    padding: 32px;
    border: 1px solid rgba(0, 0, 0, 0.05);
}

.section-title {
    font-size: 18px;
    font-weight: 700;
    color: #1e293b;
    margin-bottom: 6px;
}

.section-desc {
    font-size: 14px;
    color: #64748b;
    margin-bottom: 24px;
}

/* Form Styling */
.form-grid {
    display: grid;
    gap: 20px;
}

.form-item label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #475569;
    margin-bottom: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.required {
    color: #ef4444;
}

.modern-input, .modern-textarea {
    width: 100%;
    padding: 12px 14px;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    color: #0f172a;
    transition: all 0.2s;
    background-color: #f8fafc;
}

.modern-input:focus, .modern-textarea:focus {
    border-color: var(--primary-color, #fc6666);
    background-color: #ffffff;
    box-shadow: 0 0 0 3px var(--secondary-color, rgba(252, 102, 102, 0.1));
    outline: none;
}

.error-text {
    color: #ef4444;
    font-size: 12px;
    margin-top: 6px;
    font-weight: 500;
}

/* Tier Checkbox Card Styles */
.tier-radio-group {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
    gap: 10px;
}

.tier-radio-card {
    position: relative;
    cursor: pointer;
    background: #f8fafc;
    border: 1.5px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
}

.tier-radio-card:hover {
    border-color: var(--primary-color, #fc6666);
}

.tier-radio-card.active {
    background: var(--secondary-color, #fffafa);
    border-color: var(--primary-color, #fc6666);
}

.tier-card-content {
    display: flex;
    align-items: center;
    gap: 8px;
}

/* Checkbox Indicator (square, replaces radio circle) */
.checkbox-indicator {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    border: 2px solid #cbd5e1;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.2s;
}

.checkbox-indicator.checked {
    border-color: var(--primary-color, #fc6666);
    background: var(--primary-color, #fc6666);
}

.check-icon {
    color: white;
    font-size: 9px;
    line-height: 1;
}

.tier-name {
    font-weight: 600;
    font-size: 13px;
    color: #475569;
}

.active .tier-name {
    color: #0f172a;
}

/* Visuals Column Branding */
.visual-upload-section {
    padding: 0;
    background: white;
}

:deep(.upload-wrapper) {
    border-color: var(--primary-color, #fc6666) !important;
    background-color: var(--secondary-color, #fffafa) !important;
    width: 100% !important;
}

:deep(.icon-circle) {
    background: var(--secondary-color, #fff1f1) !important;
    color: var(--primary-color, #fc6666) !important;
}

:deep(.main-text) {
    color: var(--primary-color, #fc6666) !important;
}

/* Main Media Container (Exact uploader box style with dashed border, tight image wrap) */
.main-media-container {
    position: relative;
    width: 100%;
    border: 2px dashed var(--primary-color, #cbd5e1);
    background-color: transparent;
    border-radius: 12px;
    overflow: hidden;
    padding: 0;
    line-height: 0;
    cursor: pointer;
    transition: all 0.2s ease;
}

.main-media-container:hover {
    border-color: var(--primary-color, #fc6666);
}

.change-overlay {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(2px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;
    opacity: 0;
    transition: opacity 0.2s ease;
    gap: 8px;
    z-index: 5;
}

.main-media-container:hover .change-overlay {
    opacity: 1;
}

.change-overlay i {
    font-size: 24px;
}

.change-overlay span {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
}

.pure-media-preview {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 10px;
}

/* Dashed Action Buttons (Garis-garis, NOT bold primary) */
.dashed-action-btn {
    width: 100%;
    border: 2px dashed #cbd5e1;
    background-color: #f8fafc;
    border-radius: 10px;
    padding: 12px 16px;
    text-align: center;
    cursor: pointer;
    color: #475569;
    font-weight: 600;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    position: relative;
}

.dashed-action-btn:hover {
    border-color: var(--primary-color, #F93E3E);
    background-color: var(--secondary-color, rgba(249, 62, 62, 0.04));
    color: var(--primary-color, #F93E3E);
}

.dashed-action-btn.reset-btn {
    border-style: dashed;
    color: #64748b;
}

.file-input-hidden {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
}

/* Global Loader */
.global-loader {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(2px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

/* Responsive */
@media (max-width: 1024px) {
    .main-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 640px) {
    .page-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
    }
    .header-actions {
        width: 100%;
        justify-content: flex-start;
    }
}
</style>